class WebHeader extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // 确保 GSAP 已加载
    if (window.gsap) {
      this.initElements();
      this.initLogoAnimation();
      this.initHeaderTransparency();
      this.initMobileMenu();
    } else {
      // 如果 GSAP 未加载，等待加载完成
      window.addEventListener("DOMContentLoaded", () => {
        if (window.gsap) {
          this.initElements();
          this.initLogoAnimation();
          this.initHeaderTransparency();
          this.initMobileMenu();
        }
      });
    }
  }

  initElements() {
    this.logoContainer = this.querySelector("[data-logo-container]");
    this.logoDot = this.querySelector("[data-logo-dot]");
    this.logoText = this.querySelector("[data-logo-text]");
    this.logoTrails = [
      this.querySelector("[data-logo-trail-1]"),
      this.querySelector("[data-logo-trail-2]"),
      this.querySelector("[data-logo-trail-3]"),
    ];
    this.mobileMenuTrigger = this.querySelector("[data-mobile-menu-trigger]");
    this.mobileMenu = this.querySelector("[data-mobile-menu]");

    if (!this.logoDot || !this.logoText) return;
  }

  initLogoAnimation() {
    // 移动端不执行动画
    if (window.innerWidth <= 800) {
      gsap.set(this.logoDot, { opacity: 1 });
      gsap.set(this.logoText, { opacity: 1, width: "auto" });
      return;
    }

    // 获取文字的完整宽度
    gsap.set(this.logoText, { opacity: 1, width: "auto" });
    const fullWidth = this.logoText.offsetWidth;

    // 设置初始状态
    gsap.set(this.logoDot, { opacity: 1 });
    gsap.set(this.logoText, {
      opacity: 1,
      width: 0,
      overflow: "hidden",
      whiteSpace: "nowrap",
    });

    // 创建展开动画时间线
    const expandTl = gsap.timeline({ paused: true });
    expandTl.to(this.logoText, {
      width: fullWidth,
      duration: 0.6,
      ease: "steps(20)", // 使用 steps 缓动实现打字机效果
    });

    // 创建收起动画时间线
    const collapseTl = gsap.timeline({ paused: true });
    collapseTl.to(this.logoText, {
      width: 0,
      duration: 0.3,
      ease: "power2.in",
    });

    let isExpanded = false;
    let isAnimating = false;

    // 添加滚动监听
    window.addEventListener("scroll", () => {
      if (window.innerWidth <= 800) return;

      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;

      // 如果正在动画中，不执行任何操作
      if (isAnimating) return;

      // 超过一屏且未展开时，展开文字
      if (scrollTop > windowHeight && !isExpanded) {
        isExpanded = true;
        isAnimating = true;
        expandTl.play().then(() => {
          isAnimating = false;
        });
      }
      // 小于一屏且已展开时，收起文字
      else if (scrollTop <= windowHeight && isExpanded) {
        isExpanded = false;
        isAnimating = true;
        collapseTl.play().then(() => {
          isAnimating = false;
        });
      }
    });

    // 添加 resize 监听
    window.addEventListener("resize", () => {
      if (window.innerWidth <= 800) {
        gsap.set(this.logoDot, { opacity: 1 });
        gsap.set(this.logoText, {
          opacity: 1,
          width: "auto",
          overflow: "visible",
        });
      } else {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        const windowHeight = window.innerHeight;

        // 更新完整宽度
        gsap.set(this.logoText, { width: "auto" });
        const newFullWidth = this.logoText.offsetWidth;

        // 根据当前滚动位置设置状态
        if (scrollTop > windowHeight) {
          gsap.set(this.logoDot, { opacity: 1 });
          gsap.set(this.logoText, {
            opacity: 1,
            width: newFullWidth,
            overflow: "hidden",
            whiteSpace: "nowrap",
          });
          isExpanded = true;
        } else {
          gsap.set(this.logoDot, { opacity: 1 });
          gsap.set(this.logoText, {
            opacity: 1,
            width: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
          });
          isExpanded = false;
        }
      }
    });
  }

  initHeaderTransparency() {
    const header = this.querySelector("[data-header]");
    const caseSection = document.querySelector(".case-section");
    const whatWeDoSection = document
      .querySelector(".content .solution-list")
      .closest(".content");

    if (!header || !caseSection || !whatWeDoSection) return;

    ScrollTrigger.create({
      trigger: caseSection,
      start: "top top",
      end: "+=400%",
      onEnter: () => {
        gsap.to(header.querySelector("div"), {
          borderColor: "transparent",
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(header, {
          backgroundColor: "transparent",
          duration: 0.3,
          ease: "power2.out",
        });
      },
      onLeave: () => {
        gsap.to(header.querySelector("div"), {
          borderColor: "rgba(255, 255, 255, 0.3)",
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(header, {
          backgroundColor: "#000000",
          duration: 0.3,
          ease: "power2.out",
        });
      },
      onEnterBack: () => {
        gsap.to(header.querySelector("div"), {
          borderColor: "transparent",
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(header, {
          backgroundColor: "transparent",
          duration: 0.3,
          ease: "power2.out",
        });
      },
      onLeaveBack: () => {
        gsap.to(header.querySelector("div"), {
          borderColor: "rgba(255, 255, 255, 0.3)",
          duration: 0.3,
          ease: "power2.out",
        });
        gsap.to(header, {
          backgroundColor: "#000000",
          duration: 0.3,
          ease: "power2.out",
        });
      },
    });
  }

  initMobileMenu() {
    if (!this.mobileMenuTrigger || !this.mobileMenu) return;

    let isOpen = false;
    const menuTimeline = gsap.timeline({ paused: true });
    const menuIcon = this.mobileMenuTrigger.querySelector(".menu-icon");
    const closeIcon = this.mobileMenuTrigger.querySelector(".close-icon");

    // 设置初始状态
    gsap.set(this.mobileMenu, {
      display: "none",
      opacity: 0,
    });

    // 创建菜单动画
    menuTimeline
      .set(this.mobileMenu, {
        display: "block",
      })
      .to(this.mobileMenu, {
        opacity: 1,
        duration: 0.3,
        ease: "power2.out",
      })
      .from(
        this.mobileMenu.querySelectorAll("a"),
        {
          y: 30,
          opacity: 0,
          duration: 0.4,
          stagger: 0.1,
          ease: "power2.out",
        },
        "-=0.1"
      );

    const toggleMenu = () => {
      isOpen = !isOpen;
      if (isOpen) {
        menuIcon.classList.add("hidden");
        closeIcon.classList.remove("hidden");
        menuTimeline.play();
        document.body.classList.add("menu-open");
        // 记住当前滚动位置
        this.scrollPosition = window.pageYOffset;
      } else {
        menuIcon.classList.remove("hidden");
        closeIcon.classList.add("hidden");
        menuTimeline.reverse();
        document.body.classList.remove("menu-open");
        // 恢复滚动位置
        window.scrollTo(0, this.scrollPosition);
      }
    };

    // 添加点击事件
    this.mobileMenuTrigger.addEventListener("click", toggleMenu);

    // 添加菜单项点击事件
    this.mobileMenu.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", () => {
        isOpen = false;
        menuIcon.classList.remove("hidden");
        closeIcon.classList.add("hidden");
        menuTimeline.reverse();
        document.body.classList.remove("menu-open");
        // 恢复滚动位置
        window.scrollTo(0, this.scrollPosition);
      });
    });
  }
}

customElements.define("web-header", WebHeader);
